import React, { useState, useEffect } from 'react';
import MarketCarousel from './MarketCarousel';
import MarketCard from './MarketCard';
import { useNavigate } from 'react-router-dom';

const sampleMarkets = [
  { id: 2, asset: 'ETH/USD', horizon: '5m',  forecast: 'Down', confidence: 60, nextSettlement: '45m' },
  { id: 3, asset: 'SOL/USD', horizon: '5m', forecast: 'Up',   confidence: 85, nextSettlement: '23h' },
  { id: 1, asset: 'BTC/USD', horizon: '5m',  forecast: 'Up',   confidence: 75, nextSettlement: '2m'  },
  { id: 4, asset: 'ADA/USD', horizon: '5m',  forecast: 'Down', confidence: 55, nextSettlement: '1m'  },
  { id: 5, asset: 'DOGE/USD',horizon: '5m',  forecast: 'Up',   confidence: 65, nextSettlement: '50m' },
];

export default function MarketLobby() {

  const navigate = useNavigate();

  const [markets, setMarkets] = useState([]);
  const [animatedIndex, setAnimatedIndex] = useState(0);

  useEffect(() => {
    setMarkets(sampleMarkets);
    const interval = setInterval(() => {
      setAnimatedIndex(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const introPhrases = [
    { text: "CHALLENGE AI", color: "from-violet-400 to-pink-500" },
    { text: "PREDICT MARKETS", color: "from-pink-500 to-red-500" },
    { text: "EARN REWARDS", color: "from-red-500 to-orange-500" }
  ];

  return (
    <section id="lobby" className="my-8 font-mono">
      <div className="-mt-24 mb-8 md:mb-12">
{/* Mobile view - Redesigned */}
<div className="md:hidden">
  <div className="relative rounded-2xl overflow-hidden">
    {/* Background gradient with improved opacity */}
    <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black z-0"></div>
    
    {/* Decorative elements */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500 opacity-10 rounded-full blur-xl translate-x-16 -translate-y-10"></div>
    <div className="absolute bottom-0 left-0 w-24 h-24 bg-violet-500 opacity-10 rounded-full blur-xl -translate-x-10 translate-y-10"></div>
    <div className="absolute inset-0 rounded-2xl border border-gray-800 shadow-inner z-10"></div>

    <div className="relative z-20 pt-8 pb-10 px-5">
      {/* Header section */}
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center space-x-3">
          <div className="h-2 w-2 rounded-full bg-pink-500 animate-pulse"></div>
          <div className="text-sm text-gray-400 font-medium">sys.prediction.ai</div>
        </div>
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-pink-500 to-violet-500 opacity-70 flex items-center justify-center">
          <div className="h-4 w-4 rounded-full bg-black"></div>
        </div>
      </div>

      {/* Dynamic title section */}
      <div className="h-16 mb-8 flex items-center">
        <span className="text-gray-500 font-mono mr-2">&gt;</span>
        <div className="relative h-full flex items-center">
          {introPhrases.map((phrase, idx) => (
            <h3
              key={idx}
              className={`
                text-2xl font-black tracking-tight
                text-transparent bg-clip-text bg-gradient-to-r ${phrase.color}
                absolute transition-all duration-700 ease-in-out
                ${idx === animatedIndex
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'}
              `}
            >
              {phrase.text}
            </h3>
          ))}
        </div>
      </div>

{/* Feature panel with improved spacing */}
<div className="relative mb-8 rounded-xl overflow-hidden backdrop-blur-sm">
  <div className="absolute inset-0 bg-black opacity-60"></div>
  <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 to-gray-900/30"></div>
  <div className="relative py-6 px-4">
    <p className="text-gray-200 leading-relaxed mb-6 font-light">
      Clash with cutting‑edge <span className="text-pink-400 font-medium">AI systems</span> in financial prediction 
      or test your luck in our <span className="text-emerald-400 font-medium">Crypto Casino</span>.
    </p>
    
    {/* Feature grid with innovative styling */}
    <div className="grid grid-cols-2 gap-5 mt-2">
      {[
        { 
          title: "Predictions", 
          value: "5m to 24h",
          icon: (
            <svg className="w-3 h-3 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          accent: "from-violet-500/20 to-violet-600/5"
        },
        { 
          title: "Assets", 
          value: "Crypto + NFT",
          icon: (
            <svg className="w-3 h-3 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
          accent: "from-pink-500/20 to-pink-600/5"
        },
        { 
          title: "AI Models", 
          value: "Neural Nets",
          icon: (
            <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
          accent: "from-cyan-500/20 to-cyan-600/5"
        },
        { 
          title: "Casino", 
          value: "Scatter Slots",
          icon: (
            <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          ),
          accent: "from-emerald-500/20 to-emerald-600/5"
        }
      ].map((item, index) => (
        <div 
          key={index} 
          className="relative overflow-hidden group"
        >
          {/* Background with gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${item.accent} rounded-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
          
          {/* Border effect */}
          <div className="absolute inset-0 rounded-lg border border-gray-800/30"></div>
          
          {/* Content with glass effect */}
          <div className="relative bg-black/40 backdrop-blur-sm rounded-lg p-3 h-full">
            <div className="flex items-center mb-2">
              <div className="mr-2 p-1 rounded bg-black/30 backdrop-blur-sm">
                {item.icon}
              </div>
              <p className="text-xs text-gray-400 font-medium tracking-wide">{item.title}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">{item.value}</p>
              <div className="h-1 w-1 rounded-full bg-gray-700 group-hover:bg-gray-500 transition-colors duration-300"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

      {/* Stats section with card design */}
      <div className="space-y-4 mb-8">
        {/* Win Rate - Featured stat */}
        <div className="bg-gradient-to-br from-black/90 to-gray-900/90 p-5 rounded-xl border border-gray-800/50 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-violet-300 mb-1 font-medium">Win Rate</p>
              <div className="flex items-center">
                <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">45%</span>
                <span className="ml-2 text-green-400 text-xs font-medium">↑2.3%</span>
              </div>
            </div>
            <div className="relative h-14 w-14">
              <svg className="h-14 w-14 -rotate-90">
                <circle cx="28" cy="28" r="24" fill="transparent" stroke="#1a1a1a" strokeWidth="4"/>
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  fill="transparent"
                  stroke="url(#gradient-winrate)"
                  strokeWidth="4"
                  strokeDasharray="150.72"
                  strokeDashoffset="82.9"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="gradient-winrate" gradientTransform="rotate(90)">
                    <stop offset="0%" stopColor="#a78bfa"/>
                    <stop offset="100%" stopColor="#ec4899"/>
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-400">vs AI</span>
              </div>
            </div>
          </div>
        </div>

        {/* Smaller stats in a row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Traders */}
          <div className="bg-gradient-to-br from-black/90 to-gray-900/90 p-4 rounded-xl border border-gray-800/50 shadow-lg">
            <p className="text-xs uppercase tracking-wider text-red-300 mb-2 font-medium">Traders</p>
            <div>
              <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">1,243</span>
              <div className="flex items-center mt-2">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 mr-1.5"></span>
                <span className="text-xs text-gray-400">521 online</span>
              </div>
            </div>
          </div>

          {/* 24h Volume */}
          <div className="bg-gradient-to-br from-black/90 to-gray-900/90 p-4 rounded-xl border border-gray-800/50 shadow-lg">
            <p className="text-xs uppercase tracking-wider text-pink-300 mb-2 font-medium">24h Vol</p>
            <div>
              <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-red-400">$1.4M</span>
              <div className="flex items-center mt-2 text-xs text-gray-400">
                <span className="mr-1.5 text-green-400">↑12%</span>
                <span>daily</span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Volume */}
        <div className="bg-gradient-to-br from-black/90 to-gray-900/90 p-5 rounded-xl border border-gray-800/50 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-indigo-300 mb-1 font-medium">Total Volume</p>
              <div className="flex items-center">
                <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400">$2.5M</span>
                <span className="ml-2 text-xs text-gray-400">all time</span>
              </div>
            </div>
            <div className="h-12 flex items-end space-x-1.5">
              {[40, 28, 65, 48, 72, 58].map((h, i) => (
                <div
                  key={i}
                  className="w-1.5 rounded-t-sm bg-gradient-to-t from-indigo-500 to-violet-500"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="-mt-4 mb-4 text-xs text-red-500 flex justify-center italic">
    Placeholder stats until ARENA goes live.
  </p>
{/* CTA section */}
<div className="pt-2">
  <div className="flex space-x-3 mb-3">
    {/* Enter Arena Button */}
    <button 
      onClick={() => navigate('/')} 
      className="w-1/2 py-4 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 shadow-lg relative overflow-hidden group"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="relative flex items-center justify-center space-x-2">
        <span className="text-sm md:text-lg font-bold text-white">
          ENTER ARENA
        </span>
        <svg className="h-4 w-4 md:h-5 md:w-5 text-white transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </div>
    </button>
    
    {/* Enter Casino Button */}
    <button 
      onClick={() => navigate('/casino')} 
      className="w-1/2 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 shadow-lg relative overflow-hidden group"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="relative flex items-center justify-center space-x-2">
        <span className="text-sm md:text-lg font-bold text-white">
          ENTER CASINO
        </span>
        <svg className="h-4 w-4 md:h-5 md:w-5 text-white transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      </div>
    </button>
  </div>
  
  {/* Whitepaper Button */}
  <a 
    href="https://prediction-arena.gitbook.io/prediction-arena" 
    target="_blank" 
    rel="noopener noreferrer"
    className="w-full py-3 rounded-xl bg-gradient-to-r from-gray-700 to-gray-900 shadow-lg relative overflow-hidden group flex items-center justify-center"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    <div className="relative flex items-center justify-center space-x-2">
      <span className="text-sm font-medium text-gray-300">
        WHITEPAPER
      </span>
      <svg className="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </div>
  </a>
</div>
    </div>
  </div>
</div>

        {/* Desktop view */}
        <div className="hidden md:block p-6 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl">
          <div className="flex flex-col md:flex-row items-center">
            <div className="w-full md:w-3/4 space-y-4">
              <h3 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                AI VERSUS HUMAN
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Clash with cutting‑edge AI in real‑time prediction markets where your financial intuition 
                meets machine learning precision. Every forecast is a battle of intelligence.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Place your bets across multiple timeframes from minutes to days, on a variety of crypto assets. 
                Analyze patterns, challenge the algorithm, and earn rewards when your predictions outperform the AI.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 px-4 py-2 rounded-md border border-gray-700">
                  <span className="text-pink-400 font-bold">45%</span>
                  <span className="text-gray-400 ml-2 text-sm">Average Win Rate</span>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 px-4 py-2 rounded-md border border-gray-700">
                  <span className="text-violet-400 font-bold">$2.5M</span>
                  <span className="text-gray-400 ml-2 text-sm">Total Volume</span>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 px-4 py-2 rounded-md border border-gray-700">
                  <span className="text-red-400 font-bold">1,243</span>
                  <span className="text-gray-400 ml-2 text-sm">Active Traders</span>
                </div>
              </div>
            </div>
            <div className="hidden md:block md:w-1/4">
              <div className="relative h-40 w-40 mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500 via-pink-500 to-red-500 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute inset-2 bg-gray-900 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">
                    LIVE
                  </span>
                </div>
                
              </div>
              
            </div>
            
          </div>
          <p className="mt-2 text-xs text-red-500 flex justify-center italic">
            Placeholder stats until ARENA goes live.
          </p>
        </div>
      </div>

      {/* Section title */}
      <div className="flex items-center mb-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Live Markets
            </h3>
            <div className="flex-grow h-px ml-4 bg-gradient-to-r from-violet-500/50 via-pink-500/50 to-transparent"></div>
            <div className="ml-2 px-2 py-1 bg-black bg-opacity-40 rounded text-xs text-pink-400 border border-pink-500/30">
              Updated Now
            </div>
          </div>

      {/* Carousel & grid */}
      <div className="mb-16 block md:hidden h-96">
        <MarketCarousel markets={markets} />
      </div>
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {markets.map(m => (
          <MarketCard key={m.id} market={m} />
        ))}
      </div>

      <style jsx global>{`
        .bg-clip-path-diagonal {
          clip-path: polygon(0 0, 100% 0, 100% 100%, 0 70%);
        }
      `}</style>
    </section>
  );
}
